// The video block as an author's preview shows it — ADM.044, one implementation for the three
// content screens that share the block editor.
//
// PRD 07 §3 · 10 §3.7 · 11 §3 carry the SAME clause word for word: "미리보기 패널에서 영상 URL은
// 평문·링크 텍스트가 아니라 임베드된 재생 가능 플레이어로 렌더한다 … 인식할 수 없는 URL이면
// '영상 보기' 외부 링크 카드로 폴백한다. 이 규칙은 관리지침(07)·건강백과(10)·상담매뉴얼(11)
// 3개 화면에 동일하게 적용된다."
//
// Written once because the last time this rule lived in three places it drifted into three
// behaviors (see `video-embed.ts`): the embed half shipped to all three, the fallback half to
// none, and each screen printed the raw address instead — which the author cannot open, so a URL
// the host allow-list accepts but no pattern can embed (`youtube.com/playlist?list=…`) could not
// be checked from the preview at all.
//
// Copy comes from the caller: the three features own separate i18n namespaces, and [공통] 00
// keeps their strings separate. The empty-URL case stays with the caller too — PRD 10 v2.15 is
// explicit that "URL 미입력 블록만 종전 placeholder 유지", and each screen's placeholder differs.

import { toVideoEmbedUrl } from '@/shared/utils/video-embed';

interface VideoBlockPreviewProps {
  /** The authored video URL. Callers handle the empty case themselves. */
  url: string;
  /** Accessible title for the player frame — the block 제목, or a localized "video". */
  title: string;
  /** Localized "영상 보기" — the label ADM.044 names for the fallback card. */
  watchLabel: string;
}

export function VideoBlockPreview({ url, title, watchLabel }: VideoBlockPreviewProps) {
  const embed = toVideoEmbedUrl(url);

  if (embed) {
    return (
      <div style={{ position: 'relative', paddingTop: '56.25%', borderRadius: 8, overflow: 'hidden' }}>
        <iframe
          src={embed}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
        />
      </div>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      // The preview sits inside the editor — opening the author's own link must never navigate
      // them away from unsaved work, and `noopener` keeps the opened page off `window.opener`.
      rel="noreferrer noopener"
      style={{
        display: 'block',
        padding: '10px 12px',
        border: '1px solid var(--border)',
        borderRadius: 8,
        background: '#fff',
        color: 'var(--info)',
        fontSize: 13,
        fontWeight: 600,
        textDecoration: 'none',
      }}
    >
      ▶ {watchLabel}
      <span
        style={{
          display: 'block',
          marginTop: 2,
          fontSize: 11,
          fontWeight: 400,
          color: 'var(--muted-foreground)',
          wordBreak: 'break-all',
        }}
      >
        {url}
      </span>
    </a>
  );
}
